import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import Button from '../../Button/Button.jsx'
import {colors} from '../../../utils/colors.js'
import Edit04 from '../../Icons/Edit04.jsx'
import FlexContainer from '../../FlexContainer/FlexContainer.jsx'
import TextArea from '../../FormElements/TextArea/TextArea.jsx'
import {useLoading} from '../../../providers/LoadingProvider.jsx'
import css from './EditableRemarks.module.scss'

const EditableRemarks = ({
  reservationId,
  notes = '',
  onSave,
  apiUrl,
  formDataFieldName = 'remarks',
  type = 'GENERAL',
  title,
  isEditable = true,
  className = '',
  rows = 3,
  toast,
  posthog,
}) => {
  const {showLoading, hideLoading} = useLoading();
  const originalNotes = notes || '';
  const [notesText, setNotesText] = useState(originalNotes);
  const [isEditingNotes, setEditingNotes] = useState(false);

  useEffect(() => {
    setNotesText(notes || '');
  }, [notes]);

  const displayTitle = typeof title === 'function' ? title(type) : (title || 'Remarks');

  const submitRemarks = async () => {
    if (!apiUrl) {
      return;
    }

    try {
      posthog.capture('reservation_profile:remarks_save');
      showLoading();

      const formData = new FormData();
      formData.append(formDataFieldName, notesText);
      formData.append('type', type);

      const url = typeof apiUrl === 'function' ? apiUrl(reservationId) : apiUrl;
      await axios.post(url, formData);

      toast.success(`${displayTitle} have been saved`);
      setEditingNotes(false);
      
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error(error);
      toast.error('There was an error while trying to save the remarks');
    } finally {
      hideLoading();
    }
  };

  const onEditRemarksClick = () => {
    posthog.capture('reservation_profile:remarks_edit_click');
    setEditingNotes(true);
  };

  const onCancelRemarksClick = () => {
    posthog.capture('reservation_profile:remarks_edit_cancel');
    setEditingNotes(false);
    setNotesText(originalNotes);
  };

  if (!isEditable && !notesText) {
    return null;
  }

  return (
    <div className={`${css.editableRemarks} ${className}`}>
      <FlexContainer justifyContent="space-between" alignItems="center">
        <h5>{displayTitle}</h5>
        {isEditable &&
          <Button variant="ghost" onClick={onEditRemarksClick}>
            <Edit04 color={colors.primaryColor} />
          </Button>
        }
      </FlexContainer>
      {!isEditingNotes ?
        <div className={css.notesText}>{notesText || ''}</div>
       :
        <>
          <TextArea
            name="internalRemark"
            value={notesText}
            onChange={(event) => setNotesText(event.target.value)}
            rows={rows}
          />
          <FlexContainer gap={10}>
            <Button onClick={submitRemarks} size="small">Save</Button>
            <Button
              variant="tertiary"
              size="small"
              onClick={onCancelRemarksClick}
            >
              Close
            </Button>
          </FlexContainer>
        </>
      }
    </div>
  );
};

EditableRemarks.propTypes = {
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  notes: PropTypes.string,
  onSave: PropTypes.func,
  apiUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  formDataFieldName: PropTypes.string,
  type: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  isEditable: PropTypes.bool,
  className: PropTypes.string,
  rows: PropTypes.number,
};

export default EditableRemarks;

